import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Dialog, Stack } from '@mui/material';

import { zodResolver } from '@hookform/resolvers/zod/dist/zod';
import dayjs from 'dayjs';
import z from 'zod';

import { PickupOrderResponse } from '@/api/data-contracts';
import { ReactComponent as ClockIcon } from '@/assets/icons/clock-outline-icon.svg';
import { ReactComponent as TruckIcon } from '@/assets/icons/truck-outline-icon.svg';
import useSafeForm from '@/shared/lib/hooks/useSafeForm';
import AppButton from '@/shared/ui/AppButton';
import RhfCompactAutocomplete from '@/shared/ui/form-fields/RhfAutocomplete/RhfCompactAutocomplete';
import RhfCompactTextField from '@/shared/ui/form-fields/RhfTextField/RhfCompactTextField';

import { usePlacePickupOrderMutation } from '../../api/useShippingMutations';
import {
  PICKUP_MIN_WINDOW_MINUTES,
  getDefaultPickupWindow,
  getPickupFromSlots,
  getPickupToSlots,
  pickupTimeToMinutes,
} from '../../model/pickupHours';
import AwbDialogHeader from './AwbDialogHeader';
import AwbNotice from './awb/AwbNotice';
import { FieldsGrid, awbContentSx, generateAwbResultPaperSx } from './styles';

interface PlacePickupOrderDialogProps {
  open: boolean;
  group: PickupOrderResponse;
  onClose: () => void;
  onPlaced?: () => void;
}

const HOUR_PATTERN = /^\d{2}:\d{2}$/;

const SLOT_REFRESH_MS = 60_000;

const toOptions = (slots: string[]) => slots.map((slot) => ({ value: slot, label: slot }));

const PlacePickupOrderDialog: FC<PlacePickupOrderDialogProps> = ({
  open,
  group,
  onClose,
  onPlaced,
}) => {
  const { t } = useTranslation();
  const { mutateAsync, isLoading } = usePlacePickupOrderMutation();

  const schema = useMemo(
    () =>
      z
        .object({
          pickupHourFrom: z
            .string()
            .min(1, t('errors.required_field'))
            .regex(HOUR_PATTERN, t('errors.invalid_time_format')),
          pickupHourTo: z
            .string()
            .min(1, t('errors.required_field'))
            .regex(HOUR_PATTERN, t('errors.invalid_time_format')),
          observations: z.string().optional(),
        })
        .refine(
          (values) =>
            pickupTimeToMinutes(values.pickupHourTo) - pickupTimeToMinutes(values.pickupHourFrom) >=
            PICKUP_MIN_WINDOW_MINUTES,
          { path: ['pickupHourTo'], message: t('errors.pickup_window_too_short') },
        ),
    [t],
  );

  type PickupOrderFormValues = z.input<typeof schema>;

  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    const interval = setInterval(() => setNow(dayjs()), SLOT_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);
  const fromSlots = useMemo(
    () => getPickupFromSlots(group.pickupDate, now),
    [group.pickupDate, now],
  );
  const noSlotsLeft = fromSlots.length === 0;

  const methods = useSafeForm<PickupOrderFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { ...getDefaultPickupWindow(fromSlots), observations: '' },
  });

  const pickupHourFrom = useWatch({ control: methods.control, name: 'pickupHourFrom' });
  const fromOptions = useMemo(() => toOptions(fromSlots), [fromSlots]);
  const toSlots = useMemo(() => getPickupToSlots(pickupHourFrom), [pickupHourFrom]);
  const toOptionsList = useMemo(() => toOptions(toSlots), [toSlots]);

  useEffect(() => {
    const currentTo = methods.getValues('pickupHourTo');
    if (currentTo && !toSlots.includes(currentTo)) {
      methods.setValue('pickupHourTo', toSlots[0] ?? '', { shouldValidate: true });
    }
  }, [methods, toSlots]);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = methods.handleSubmit(async (values) => {
    await mutateAsync({
      pickupDate: group.pickupDate,
      pickupHourFrom: values.pickupHourFrom,
      pickupHourTo: values.pickupHourTo,
      clientId: group.clientId,
      orderType: 'Standard',
      observations: values.observations || undefined,
    });

    handleClose();
    onPlaced?.();
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        'data-testid': 'place-pickup-order-dialog',
        sx: { ...generateAwbResultPaperSx, maxWidth: '32.875rem' },
      }}
    >
      <AwbDialogHeader
        icon={<TruckIcon />}
        title={t('Order a courier')}
        onClose={handleClose}
        closeTestId="place-pickup-order-close"
      />

      {noSlotsLeft && (
        <AwbNotice data-testid="place-pickup-order-no-slots">
          {t('awb.no_pickup_slots_today')}
        </AwbNotice>
      )}

      <FormProvider {...methods}>
        <Stack gap="1rem" component="form" onSubmit={onSubmit} sx={awbContentSx}>
          <FieldsGrid columns={2}>
            <RhfCompactAutocomplete
              name="pickupHourFrom"
              label={t('Pickup from')}
              isRequired
              options={fromOptions}
              startAdornment={<ClockIcon style={{ flexShrink: 0, marginLeft: 8 }} />}
              sx={{ width: '100%' }}
              data-testid="place-pickup-order-from"
            />
            <RhfCompactAutocomplete
              name="pickupHourTo"
              label={t('Pickup to')}
              isRequired
              options={toOptionsList}
              startAdornment={<ClockIcon style={{ flexShrink: 0, marginLeft: 8 }} />}
              sx={{ width: '100%' }}
              data-testid="place-pickup-order-to"
            />
          </FieldsGrid>
          <Box
            sx={{
              '& .MuiOutlinedInput-root': { height: 'auto !important', alignItems: 'flex-start' },
            }}
          >
            <RhfCompactTextField
              name="observations"
              data-testid="place-pickup-order-observations"
              label={t('Observations')}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </Stack>
      </FormProvider>

      <Stack direction="row" justifyContent="flex-end" gap="0.5rem">
        <AppButton
          variant="secondary"
          onClick={handleClose}
          data-testid="place-pickup-order-cancel"
        >
          {t('Cancel')}
        </AppButton>
        <AppButton
          variant="primary"
          loading={isLoading}
          disabled={noSlotsLeft}
          onClick={onSubmit}
          data-testid="place-pickup-order-submit"
        >
          {t('buttons.confirm')}
        </AppButton>
      </Stack>
    </Dialog>
  );
};

export default PlacePickupOrderDialog;
